// カメラ映像の 1 コマがパスポートの写真面かを判定する（自動撮影の契機）。据置端末からの移植で、
// 判定条件と前処理を揃えてある。OpenCV と Tesseract は public/ から必要時に読み込む
const OpenCvScriptPath = '/opencv.4.13.0.js'

const TesseractWorkerPath = '/tesseract-worker.min.js'

const TesseractCorePath = '/tesseract-core'

const TesseractLangPath = '/tessdata'

const PreprocessParameter = {
  blurSize: 3,
  blockSize: 7,
  cValue: 7,
}

export class PassportDetectorLoadError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'PassportDetectorLoadError'
  }
}

type TesseractWorker = {
  recognize: (image: HTMLCanvasElement) => Promise<{ data: { text: string } }>
  terminate: () => Promise<unknown>
}

let tesseractWorkerPromise: Promise<TesseractWorker> | undefined

/**
 * 初期化済みの OpenCV を返す。まだなら `undefined`。
 *
 * `<script>` の読み込み直後は `window.cv` があっても `Mat` が生えていない
 * （WASM の初期化が終わるまで 0.1〜数秒）。
 *
 * **`window.cv` を `await` したり Promise の `resolve()` に渡してはいけない。**
 * `opencv.4.13.0.js` の `Module.then` は Module 自身をコールバックに渡す実装で、
 * Promise はそれを「まだ確定していない thenable」と見なして `then` を呼び直す。
 * これが無限に繰り返され、マイクロタスクのループでスレッドが止まる
 * （タイマーも動かないのでタイムアウトも効かない）。以前この罠を Worker 内で踏み、
 * 自動撮影が一度も発火しない状態になっていた。
 */
const getOpenCv = (): any | undefined => {
  const cv = (window as any).cv
  return cv && typeof cv.Mat === 'function' ? cv : undefined
}

/**
 * OpenCV の `<script>` を一度だけ差し込む。
 *
 * 据置端末の `<script async src="/opencv.4.13.0.js">` と同じ読み込み方。
 * **読み込み完了は待たない。** 準備ができたかは使う直前に `getOpenCv()` で見る。
 * 10MB の解析と WASM の展開は同期処理なので、実行中は 1〜2 秒ほど画面が止まることがある。
 * 海外在住フォームを表示した時点で呼ぶことで、その止まりを「フォームを読んでいる間」に寄せる。
 */
const loadOpenCvScript = (): void => {
  if (document.querySelector(`script[src="${OpenCvScriptPath}"]`)) {
    return
  }
  const script = document.createElement('script')
  script.src = OpenCvScriptPath
  script.async = true
  document.head.appendChild(script)
}

const loadTesseractWorker = (): Promise<TesseractWorker> => {
  if (tesseractWorkerPromise) {
    return tesseractWorkerPromise
  }

  const pending: Promise<TesseractWorker> = (async () => {
    const { createWorker } = await import('tesseract.js')
    return (await createWorker('eng', undefined, {
      workerPath: TesseractWorkerPath,
      corePath: TesseractCorePath,
      langPath: TesseractLangPath,
      gzip: true,
    })) as unknown as TesseractWorker
  })().catch(error => {
    // 自分が張ったものだけ消す。先に terminate されて別の読み込みが始まっていると、
    // 無条件に消せばそちらの worker が誰からも終了されないまま残る
    if (tesseractWorkerPromise === pending) {
      tesseractWorkerPromise = undefined
    }
    throw error instanceof PassportDetectorLoadError ? error : new PassportDetectorLoadError('Tesseract を読み込めませんでした', error)
  })
  tesseractWorkerPromise = pending

  return pending
}

/**
 * OpenCV と Tesseract を先に読み込む。
 *
 * 初回の判定は読み込みに数秒かかるため、海外在住フォームを表示した時点で呼んでおく。
 * 呼ばなければ最初の `detectPassport` が読み込む。
 * OpenCV は `<script>` を差し込むだけで完了を待たない。Tesseract は worker の準備まで待つ。
 * **Tesseract の読み込みに失敗したら例外を投げるだけ。** 呼び出し側は手動撮影に落とす。
 */
export const preparePassportDetector = async (): Promise<void> => {
  loadOpenCvScript()
  await loadTesseractWorker()
}

/**
 * Tesseract の worker を破棄する。カメラを閉じるときに呼ぶ。
 *
 * OpenCV の `<script>` は外さない。据置端末と同じく一度読んだら置いておく
 * （外しても WASM のメモリは解放されず、次に開くときにまた 10MB を解析することになる）。
 */
export const terminatePassportDetector = async (): Promise<void> => {
  const tesseract = tesseractWorkerPromise
  tesseractWorkerPromise = undefined
  if (tesseract) {
    await tesseract.then(instance => instance.terminate()).catch(() => undefined)
  }
}

/** 元の canvas の一部を切り出した作業用 canvas を作る */
const cropToCanvas = (
  source: HTMLCanvasElement,
  region: { x: number; y: number; width: number; height: number },
): HTMLCanvasElement | undefined => {
  const width = Math.floor(region.width)
  const height = Math.floor(region.height)
  if (width <= 0 || height <= 0) {
    return undefined
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    return undefined
  }
  context.drawImage(source, Math.floor(region.x), Math.floor(region.y), width, height, 0, 0, width, height)
  return canvas
}

/**
 * 前処理（グレースケール化 → ぼかし → 適応的二値化）を canvas に書き戻す。
 * 対象は枠内の 1/8 の小さな切り出しなので、メインスレッドで行っても数ミリ秒で終わる。
 */
const preprocess = (cv: any, canvas: HTMLCanvasElement): void => {
  const src = cv.imread(canvas)
  const gray = new cv.Mat()
  const blurred = new cv.Mat()
  const adaptive = new cv.Mat()
  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
    cv.GaussianBlur(gray, blurred, new cv.Size(PreprocessParameter.blurSize, PreprocessParameter.blurSize), 0)
    cv.adaptiveThreshold(
      blurred,
      adaptive,
      255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C,
      cv.THRESH_BINARY,
      PreprocessParameter.blockSize,
      PreprocessParameter.cValue,
    )
    cv.imshow(canvas, adaptive)
  } finally {
    src.delete()
    gray.delete()
    blurred.delete()
    adaptive.delete()
  }
}

/** 前処理をかけてから OCR する。渡した canvas は前処理で書き換わる（切り出し済みの作業用 canvas を渡すこと） */
const recognize = async (cv: any, worker: TesseractWorker, canvas: HTMLCanvasElement): Promise<string> => {
  preprocess(cv, canvas)
  const {
    data: { text },
  } = await worker.recognize(canvas)

  return text
    .trim()
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .toLowerCase()
}

/**
 * パスポートが枠に入っているかを返す。
 *
 * **渡す canvas は枠（ガイド）の内側を切り出したもの。** その中の
 * 左上（`PASSPORT` の表記）と右下（MRZ の `<<<`）を別々に OCR し、
 * どちらかが当たれば真を返す（`aipass_hotel` と同じ条件）。
 *
 * 渡した canvas は書き換えない。
 * **OpenCV の初期化がまだなら、待たずに `false` を返す**（2 秒後の次の回で判定される）。
 * **Tesseract の読み込みに失敗したら例外を投げる。** 呼び出し側は手動撮影に落とすこと。
 */
export const detectPassport = async (canvas: HTMLCanvasElement): Promise<boolean> => {
  if (!canvas.width || !canvas.height) {
    return false
  }

  loadOpenCvScript()
  const cv = getOpenCv()
  if (!cv) {
    return false
  }

  const worker = await loadTesseractWorker()

  // 左半分の上 1/4。`PASSPORT` の表記が入る位置
  const topCanvas = cropToCanvas(canvas, { x: 0, y: 0, width: canvas.width / 2, height: canvas.height / 4 })
  // 右半分の下 1/4。MRZ が入る位置
  const bottomCanvas = cropToCanvas(canvas, {
    x: canvas.width / 2,
    y: canvas.height - canvas.height / 4,
    width: canvas.width / 2,
    height: canvas.height / 4,
  })
  if (!topCanvas || !bottomCanvas) {
    return false
  }

  const [topText, bottomText] = await Promise.all([recognize(cv, worker, topCanvas), recognize(cv, worker, bottomCanvas)])
  const hasPassportLabel = topText.includes('passport')
  const hasMachineReadableZone = /([<l]{3,})/.test(bottomText)
  // 据置端末（aipass_hotel の identity-verify-camera.tsx:139）と同じく、どちらか一方を認識したら撮る
  return hasPassportLabel || hasMachineReadableZone
}
